import {
  buildSessionKey,
  compareSlots,
  DAY_ORDER,
  formatUserLabel,
  getAssignmentRangeLabel,
  getDayValueFromDate,
  getEndOfMonthDateValue,
  getMonthDateValues,
  getRecurringMonthsEndDateValue,
  getRegularAssignmentForDate,
  getSiteAccent,
  getWeekDateValues,
  isDateWithinAssignmentRange,
  isSameTrainer,
  normalizeAssignments,
  normalizeSiteSessions,
  parseTimeToMinutes,
  toDateValue,
} from "./trainerUtils";

const SLOT_ASSIGNABLE_ROLE_KEYS = ["User", "Editor", "Admin", "SuperAdmin"];

const hasSlotAssignableRole = (roles = {}) =>
  SLOT_ASSIGNABLE_ROLE_KEYS.some((roleKey) => Boolean(roles?.[roleKey]));

const ensureTrainerOption = (optionMap, trainerId, label = "Trainer") => {
  const safeTrainerId = String(trainerId || "").trim();
  if (!safeTrainerId) return null;

  const existingOption = optionMap.get(safeTrainerId);
  if (existingOption) {
    if (
      label &&
      (!existingOption.label || existingOption.label === "Trainer")
    ) {
      existingOption.label = label;
    }
    return existingOption;
  }

  const nextOption = {
    id: safeTrainerId,
    label: label || "Trainer",
    regularCount: 0,
    singleCount: 0,
    siteIds: new Set(),
    mail: "",
  };

  optionMap.set(safeTrainerId, nextOption);
  return nextOption;
};

export const buildTrainerSites = (trainerSitesData, siteOverrides = {}) => {
  const sites = Array.isArray(trainerSitesData) ? trainerSitesData : [];

  return [...sites]
    .sort((a, b) =>
      String(a?.siteName || "").localeCompare(String(b?.siteName || ""), "de"),
    )
    .map((site, index) => {
      const trainerAssignments =
        siteOverrides[site?._id] ||
        normalizeAssignments(site?.trainerAssignments);
      const regularAssignmentsMap = new Map();
      const singleAssignmentsMap = new Map();

      trainerAssignments.regular.forEach((entry) => {
        if (!entry?.sessionKey) return;
        const currentEntries = regularAssignmentsMap.get(entry.sessionKey) || [];
        regularAssignmentsMap.set(entry.sessionKey, [...currentEntries, entry]);
      });

      trainerAssignments.single.forEach((entry) => {
        if (entry?.sessionKey && entry?.date) {
          singleAssignmentsMap.set(`${entry.sessionKey}::${entry.date}`, entry);
        }
      });

      return {
        ...site,
        trainerAssignments,
        sessions: normalizeSiteSessions(site?.trainingSessions || []),
        regularAssignmentsMap,
        singleAssignmentsMap,
        accent: getSiteAccent(index),
      };
    });
};

export const buildAdminTrainerOptions = (trainerSites, trainerList = []) => {
  const optionMap = new Map();

  trainerSites.forEach((site) => {
    const siteId = String(site?._id || "").trim();
    const trainerAssignments = site?.trainerAssignments || {
      regular: [],
      single: [],
    };

    trainerAssignments.regular.forEach((assignment) => {
      const option = ensureTrainerOption(
        optionMap,
        assignment?.trainerId,
        assignment?.trainerName || assignment?.trainerLabel || "Trainer",
      );
      if (!option) return;

      if (siteId) {
        option.siteIds.add(siteId);
      }
      option.regularCount += 1;
    });

    trainerAssignments.single.forEach((assignment) => {
      const option = ensureTrainerOption(
        optionMap,
        assignment?.trainerId,
        assignment?.trainerName || assignment?.trainerLabel || "Trainer",
      );
      if (!option) return;

      if (siteId) {
        option.siteIds.add(siteId);
      }
      option.singleCount += 1;
    });
  });

  trainerList.forEach((trainer) => {
    const trainerId = String(trainer?._id || "").trim();
    const siteIds = Array.isArray(trainer?.sites)
      ? trainer.sites
          .map((site) => String(site?._id || site || "").trim())
          .filter(Boolean)
      : [];

    if (!trainerId || (!siteIds.length && !optionMap.has(trainerId))) {
      return;
    }

    const option = ensureTrainerOption(
      optionMap,
      trainerId,
      `${trainer?.firstName || ""} ${trainer?.lastName || ""}`.trim() ||
        trainer?.mail ||
        "Trainer",
    );

    if (!option) return;

    option.mail = trainer?.mail || option.mail || "";
    siteIds.forEach((siteId) => option.siteIds.add(siteId));
  });

  return [...optionMap.values()]
    .map((option) => ({
      id: option.id,
      label: option.label || option.mail || "Trainer",
      regularCount: option.regularCount,
      singleCount: option.singleCount,
      bookingCount: option.regularCount + option.singleCount,
      siteCount: option.siteIds.size,
      mail: option.mail || "",
    }))
    .sort((a, b) => {
      const bookingDiff = b.bookingCount - a.bookingCount;
      if (bookingDiff !== 0) return bookingDiff;

      const siteDiff = b.siteCount - a.siteCount;
      if (siteDiff !== 0) return siteDiff;

      return String(a.label || "").localeCompare(String(b.label || ""), "de");
    });
};

export const buildSlotAssigneeOptions = (trainerList = [], activeSiteId = "") =>
  [...(Array.isArray(trainerList) ? trainerList : [])]
    .filter((trainer) => {
      const trainerId = String(trainer?._id || "").trim();
      return trainerId && hasSlotAssignableRole(trainer?.roles);
    })
    .map((trainer) => {
      const siteIds = Array.isArray(trainer?.sites)
        ? trainer.sites
            .map((site) => String(site?._id || site || "").trim())
            .filter(Boolean)
        : [];
      const uniqueSiteIds = [...new Set(siteIds)];

      return {
        id: String(trainer?._id || "").trim(),
        label: formatUserLabel(trainer),
        mail: trainer?.mail || "",
        siteCount: uniqueSiteIds.length,
        isAssignedToSite: activeSiteId
          ? uniqueSiteIds.includes(String(activeSiteId))
          : false,
      };
    })
    .sort((a, b) => {
      const siteMatchDiff = Number(b.isAssignedToSite) - Number(a.isAssignedToSite);
      if (siteMatchDiff !== 0) return siteMatchDiff;

      return String(a.label || "").localeCompare(String(b.label || ""), "de");
    });

export const getSlotStateForDate = ({
  site,
  session,
  dateValue,
  currentUserId,
}) => {
  const sessionKey = buildSessionKey(session);
  const singleAssignment = site?.singleAssignmentsMap?.get(
    `${sessionKey}::${dateValue}`,
  );
  const regularAssignment = getRegularAssignmentForDate(
    site?.regularAssignmentsMap?.get(sessionKey) || [],
    dateValue,
  );
  const assignment = singleAssignment || regularAssignment || null;
  const assignmentMode = singleAssignment
    ? "single"
    : assignment
    ? "regular"
    : null;
  const status = !assignment
    ? "open"
    : isSameTrainer(assignment?.trainerId, currentUserId)
    ? "mine"
    : "occupied";

  return {
    date: dateValue,
    siteId: site?._id || "",
    siteName: site?.siteName || "Standort",
    siteAccent: site?.accent || getSiteAccent(0),
    session,
    sessionKey,
    assignment,
    assignmentMode,
    status,
  };
};

export const buildSlotsByDate = ({
  activeSites,
  currentUserId,
  visibleDateValues,
}) => {
  const slotMap = new Map();

  visibleDateValues.forEach((dateValue) => {
    const dayValue = getDayValueFromDate(dateValue);
    const daySlots = activeSites
      .flatMap((site) =>
        site.sessions
          .filter((session) => session.day === dayValue)
          .map((session) =>
            getSlotStateForDate({
              site,
              session,
              dateValue,
              currentUserId,
            }),
          ),
      )
      .sort(compareSlots);

    slotMap.set(dateValue, daySlots);
  });

  return slotMap;
};

export const buildRangeSummary = (visibleDateValues, slotsByDate) =>
  visibleDateValues.reduce(
    (summary, dateValue) => {
      (slotsByDate.get(dateValue) || []).forEach((slot) => {
        summary.total += 1;
        if (slot.status === "open") summary.open += 1;
        if (slot.status === "mine") summary.mine += 1;
        if (slot.status === "occupied") summary.occupied += 1;
      });
      return summary;
    },
    { total: 0, open: 0, mine: 0, occupied: 0 },
  );

const compareSummaryEntries = (a, b) => {
  const startDiff = parseTimeToMinutes(a.start) - parseTimeToMinutes(b.start);
  if (startDiff !== 0) return startDiff;

  const endDiff = parseTimeToMinutes(a.end) - parseTimeToMinutes(b.end);
  if (endDiff !== 0) return endDiff;

  const siteDiff = String(a.siteName || "").localeCompare(
    String(b.siteName || ""),
    "de",
  );
  if (siteDiff !== 0) return siteDiff;

  const typeDiff = String(a.occurrenceType || "").localeCompare(
    String(b.occurrenceType || ""),
  );
  if (typeDiff !== 0) return typeDiff;

  return String(a.title || "").localeCompare(String(b.title || ""), "de");
};

const buildSummaryEntryKey = (entry) =>
  [
    entry.occurrenceType || "",
    entry.siteId || "",
    entry.sessionKey || "",
    entry.date || "",
    entry.startDate || "",
    entry.endDate || "",
  ].join("::");

export const buildTrainerSummary = (
  trainerSites,
  currentUserId,
  monthAnchor = new Date(),
) => {
  const regularByDay = {};
  const regularEntries = [];
  const regularEntriesByDay = {};
  const singles = [];
  let weeklyMinutes = 0;

  DAY_ORDER.forEach((day) => {
    regularByDay[day] = [];
    regularEntriesByDay[day] = [];
  });

  const now = new Date();
  const todayValue = toDateValue(now);
  const currentWeekDateValues = getWeekDateValues(now);
  const summaryMonthAnchor =
    monthAnchor instanceof Date && !Number.isNaN(monthAnchor.getTime())
      ? new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1, 12)
      : new Date(now.getFullYear(), now.getMonth(), 1, 12);
  const currentMonthDateValues = getMonthDateValues(summaryMonthAnchor);
  const currentMonthDateSet = new Set(currentMonthDateValues);
  const monthLabel = summaryMonthAnchor.toLocaleDateString("de-DE", {
    month: "long",
    year: "numeric",
  });
  const monthPrefix = `${summaryMonthAnchor.getFullYear()}-${String(
    summaryMonthAnchor.getMonth() + 1,
  ).padStart(2, "0")}`;

  const isRegularEntryInSummaryMonth = (entry) =>
    currentMonthDateValues.some(
      (dateValue) =>
        getDayValueFromDate(dateValue) === entry.day &&
        isDateWithinAssignmentRange(dateValue, entry),
    );
  const isSingleCoveredByRegular = (singleEntry) =>
    (regularEntriesByDay[singleEntry.day] || []).some(
      (regularEntry) =>
        regularEntry.siteId === singleEntry.siteId &&
        regularEntry.sessionKey === singleEntry.sessionKey &&
        isSameTrainer(regularEntry.trainerId, singleEntry.trainerId) &&
        isDateWithinAssignmentRange(singleEntry.date, regularEntry),
    );

  trainerSites.forEach((site) => {
    const trainerAssignments = site.trainerAssignments || {
      regular: [],
      single: [],
    };

    trainerAssignments.regular.forEach((assignment) => {
      if (!isSameTrainer(assignment?.trainerId, currentUserId)) return;

      const duration =
        parseTimeToMinutes(assignment.end) - parseTimeToMinutes(assignment.start);
      const entry = {
        ...assignment,
        siteId: site?._id || "",
        siteName: site.siteName,
        siteAccent: site.accent,
        duration: Math.max(0, duration),
        rangeLabel: getAssignmentRangeLabel(assignment),
      };

      regularEntries.push(entry);

      if (assignment.day && regularEntriesByDay[assignment.day]) {
        regularEntriesByDay[assignment.day].push(entry);
      }

      const appliesThisWeek = currentWeekDateValues.some(
        (dateValue) =>
          getDayValueFromDate(dateValue) === assignment.day &&
          isDateWithinAssignmentRange(dateValue, assignment),
      );

      if (appliesThisWeek) {
        weeklyMinutes += entry.duration;
      }
    });

    trainerAssignments.single.forEach((assignment) => {
      if (!isSameTrainer(assignment?.trainerId, currentUserId)) return;

      const duration =
        parseTimeToMinutes(assignment.end) - parseTimeToMinutes(assignment.start);
      singles.push({
        ...assignment,
        siteId: site?._id || "",
        siteName: site.siteName,
        siteAccent: site.accent,
        duration: Math.max(0, duration),
      });
    });
  });

  regularEntries.forEach((entry) => {
    if (entry.day && regularByDay[entry.day] && isRegularEntryInSummaryMonth(entry)) {
      regularByDay[entry.day].push(entry);
    }
  });

  singles.sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const dedupedSingles = singles.filter((entry) => !isSingleCoveredByRegular(entry));

  const monthSingles = dedupedSingles.filter((entry) =>
    currentMonthDateSet.has(entry.date),
  );
  const upcomingSingles = dedupedSingles.filter(
    (entry) => (entry.date || "") >= todayValue,
  );
  const monthSingleMinutes = monthSingles.reduce(
    (minutes, entry) => minutes + entry.duration,
    0,
  );
  const regularCount = Object.values(regularByDay).reduce(
    (count, entries) => count + entries.length,
    0,
  );
  const monthActivityDays = currentMonthDateValues.reduce((days, dateValue) => {
    const dayValue = getDayValueFromDate(dateValue);
    const regularOccurrences = (regularEntriesByDay[dayValue] || [])
      .filter((entry) => isDateWithinAssignmentRange(dateValue, entry))
      .map((entry) => ({
        ...entry,
        date: dateValue,
        occurrenceType: "regular",
        entryKey: buildSummaryEntryKey({
          ...entry,
          date: dateValue,
          occurrenceType: "regular",
        }),
      }));
    const singleOccurrences = monthSingles
      .filter((entry) => entry.date === dateValue)
      .map((entry) => ({
        ...entry,
        occurrenceType: "single",
        entryKey: buildSummaryEntryKey({
          ...entry,
          occurrenceType: "single",
        }),
      }));
    const entries = [...regularOccurrences, ...singleOccurrences].sort(
      compareSummaryEntries,
    );

    if (!entries.length) {
      return days;
    }

    return [
      ...days,
      {
        date: dateValue,
        count: entries.length,
        totalMinutes: entries.reduce(
          (minutes, entry) => minutes + entry.duration,
          0,
        ),
        entries,
      },
    ];
  }, []);
  const monthRegularCount = monthActivityDays.reduce(
    (count, day) =>
      count +
      day.entries.filter((entry) => entry.occurrenceType === "regular").length,
    0,
  );
  const monthSingleCount = monthActivityDays.reduce(
    (count, day) =>
      count + day.entries.filter((entry) => entry.occurrenceType === "single").length,
    0,
  );
  const monthRegularMinutes = monthActivityDays.reduce(
    (minutes, day) =>
      minutes +
      day.entries
        .filter((entry) => entry.occurrenceType === "regular")
        .reduce((sum, entry) => sum + entry.duration, 0),
    0,
  );

  return {
    monthLabel,
    monthPrefix,
    regularByDay,
    regularCount,
    weeklyMinutes,
    upcomingSingles,
    monthSingles,
    monthSingleCount,
    monthSingleMinutes,
    monthRegularCount,
    monthRegularMinutes,
    monthActivityDays,
    monthTrainingCount: monthRegularCount + monthSingleCount,
    monthTotalMinutes: monthRegularMinutes + monthSingleMinutes,
  };
};

export const buildRegularBookingPreview = (
  activeSlot,
  regularBookingConfig,
) => {
  if (!activeSlot?.date) {
    return {
      startDate: "",
      endDate: "",
      rangeLabel: "",
      helperLabel: "",
    };
  }

  const startDate = activeSlot.date;
  let endDate = "";

  if (regularBookingConfig.durationType === "month_end") {
    endDate = getEndOfMonthDateValue(startDate);
  }

  if (regularBookingConfig.durationType === "months") {
    endDate = getRecurringMonthsEndDateValue(
      startDate,
      regularBookingConfig.months,
    );
  }

  return {
    startDate,
    endDate,
    rangeLabel: getAssignmentRangeLabel(
      { startDate, endDate },
      { long: true },
    ),
    helperLabel:
      regularBookingConfig.durationType === "indefinite"
        ? "Wiederholt sich ab dem ausgewählten Termin, bis du die Zuordnung änderst."
        : regularBookingConfig.durationType === "month_end"
        ? "Wiederholt sich bis zum Monatsende."
        : `Wiederholt sich für ${regularBookingConfig.months} Monat${
            regularBookingConfig.months === 1 ? "" : "e"
          }.`,
  };
};

export const getDayOverview = (daySlots = []) => {
  const openCount = daySlots.filter((slot) => slot.status === "open").length;
  const mineCount = daySlots.filter((slot) => slot.status === "mine").length;
  const occupiedCount = daySlots.filter(
    (slot) => slot.status === "occupied",
  ).length;
  const siteCount = new Set(
    daySlots.map((slot) => slot.siteId).filter(Boolean),
  ).size;
  const firstSlot = daySlots[0] || null;
  const summaryParts = [];

  if (daySlots.length > 0) {
    summaryParts.push(
      `${daySlots.length} Training${daySlots.length === 1 ? "" : "s"}`,
    );
  }

  if (firstSlot?.session?.start) {
    summaryParts.push(`ab ${firstSlot.session.start}`);
  }

  if (siteCount > 1) {
    summaryParts.push(`${siteCount} Standorte`);
  }

  return {
    firstSlot,
    bookedCount: mineCount + occupiedCount,
    mineCount,
    occupiedCount,
    openCount,
    totalCount: daySlots.length,
    summaryLabel: summaryParts.join(" · ") || "Kein Training geplant",
  };
};

export const formatDurationLabel = (minutes) => {
  const safeMinutes = Math.max(0, Math.floor(Number(minutes) || 0));
  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  if (hours === 0 && remainingMinutes === 0) {
    return "0 Std.";
  }

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} Std.`);
  }

  if (remainingMinutes > 0) {
    parts.push(`${remainingMinutes} Min.`);
  }

  return parts.join(" ");
};
